import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Textarea, Button } from '@components';
import { FaqApiError } from '../services/faqService';
import { FAQ_ANSWER_MAX_LENGTH, FAQ_QUESTION_MAX_LENGTH, FaqEntryInput } from '../types';

interface FaqEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FaqEntryInput) => Promise<unknown>;
  // Without an entry the modal creates a new one
  entry: FaqEntryInput | null;
  isSaving: boolean;
  // Prefills the question of a new entry, e.g. from an unanswered question
  initialQuestion?: string;
  title?: string;
  submitLabel?: string;
}

export const FaqEntryModal: React.FC<FaqEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  entry,
  isSaving,
  initialQuestion = '',
  title,
  submitLabel,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FaqEntryInput>({ question: '', answer: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ question: entry?.question ?? initialQuestion, answer: entry?.answer ?? '' });
      setFieldErrors({});
      setGeneralError(null);
    }
  }, [entry, initialQuestion, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setGeneralError(null);
    try {
      await onSubmit({ question: formData.question.trim(), answer: formData.answer.trim() });
      onClose();
    } catch (err) {
      if (err instanceof FaqApiError && Object.keys(err.fieldErrors).length > 0) {
        setFieldErrors(err.fieldErrors);
      } else {
        setGeneralError(err instanceof Error ? err.message : t('knowledgeBase.faq.saveFailed'));
      }
    }
  };

  const canSubmit = formData.question.trim() !== '' && formData.answer.trim() !== '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {title ?? (entry ? t('knowledgeBase.faq.editEntry') : t('knowledgeBase.faq.addEntry'))}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            id="faq-question"
            label={t('knowledgeBase.faq.question')}
            value={formData.question}
            onChange={e => setFormData({ ...formData, question: e.target.value })}
            placeholder={t('knowledgeBase.faq.questionPlaceholder')}
            rows={2}
            maxLength={FAQ_QUESTION_MAX_LENGTH}
            showCount
            required
            state={fieldErrors.question ? 'error' : 'default'}
            errorText={fieldErrors.question}
          />

          <Textarea
            id="faq-answer"
            label={t('knowledgeBase.faq.answer')}
            value={formData.answer}
            onChange={e => setFormData({ ...formData, answer: e.target.value })}
            placeholder={t('knowledgeBase.faq.answerPlaceholder')}
            helperText={t('knowledgeBase.faq.answerHelp')}
            rows={6}
            maxLength={FAQ_ANSWER_MAX_LENGTH}
            showCount
            required
            state={fieldErrors.answer ? 'error' : 'default'}
            errorText={fieldErrors.answer}
          />

          {generalError && <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>}

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="gradient-multi"
              className="flex items-center gap-2"
              disabled={!canSubmit || isSaving}
            >
              <span className="material-icons">{entry ? 'save' : 'add'}</span>
              {submitLabel ??
                (entry ? t('knowledgeBase.saveChanges') : t('knowledgeBase.faq.addEntry'))}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
